import React, { useRef } from 'react';
import { FormikProps } from 'formik';
import {
  CountryIso2,
  CountrySelector,
  defaultCountries,
  DialCodePreview,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import {
  Box,
  InputHelperText,
  InputLabel,
  InputProps,
  TextInput,
} from 'app/shared/components/atoms';
import { Grid, GridItem } from 'app/shared/components/atoms';

import { useReactIntlPhoneStyleProps } from './useReactIntlPhoneStyleProps';

import 'react-international-phone/style.css';

interface Props extends InputProps, FormikProps<any> {
  label: string;
  phoneFieldName?: string;
  validationErrorIntlId?: string;
  helperText?: string | React.ReactNode | null;
  isDisabled?: boolean;
  inputBackgroundColor?: string;
  labelColor?: string;
  validationErrors?: { [key: string]: string };
  countryCodes?: string[];
  selectedCountryCode?: string;
  isRequired?: boolean;
  'data-qaid'?: string;
}

export const MobilePhoneField: React.FC<React.PropsWithChildren<Props>> = ({
  helperText,
  label,
  phoneFieldName = 'mobile',
  validationErrorIntlId = 'form.validations.mobile.invalid',
  isSuccessful = false,
  isDisabled = false,
  className,
  backgroundColor,
  inputBackgroundColor,
  labelColor,
  values,
  errors,
  touched,
  handleBlur,
  countryCodes,
  validationErrors = {},
  setFieldValue,
  selectedCountryCode,
  invertColors,
  isRequired,
  'data-qaid': qaId = 'mobile',
  ...props
}) => {
  const { borderRadius, ruler, dimensions } = useCurrentTheme();

  const {
    buttonContentWrapperStyle,
    buttonStyle,
    countrySelectorStyle,
    dialCodeStyleProps,
    dropdownArrowStyle,
    dropdownStyleProps,
    flagStyle,
  } = useReactIntlPhoneStyleProps(backgroundColor);

  const dropDownAndPrefixRef = useRef<HTMLDivElement>(null);

  const paddingLeft = Math.floor(
    dropDownAndPrefixRef?.current
      ? dropDownAndPrefixRef.current.clientWidth / 4 + 6
      : 22
  );

  const countries = countryCodes
    ? defaultCountries.filter((country) => {
        const { iso2 } = parseCountry(country);
        return countryCodes.includes(iso2);
      })
    : defaultCountries;

  const { formatMessage } = useIntl();

  const validationErrorMsg = validationErrors[phoneFieldName]
    ? formatMessage({ id: validationErrorIntlId })
    : undefined;
  const errorMsg =
    (!!errors[phoneFieldName] &&
      touched[phoneFieldName] &&
      errors[phoneFieldName]) ||
    (!!errors.countryCode && touched.countryCode && errors.countryCode) ||
    validationErrorMsg;

  const hasError = !!errorMsg;

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: selectedCountryCode || 'us',
      value: values[phoneFieldName],
      countries,
      onChange: ({ country, inputValue }) => {
        setFieldValue(phoneFieldName, inputValue);
        setFieldValue('countryCode', country.iso2);
      },
      disableDialCodeAndPrefix: true,
      disableCountryGuess: true,
      disableDialCodePrefill: true,
    });

  return (
    <Box flexDirection="row">
      <Box
        position="relative"
        w="100%"
        flexDirection="column"
        alignItems="stretch"
        className={className}
      >
        <InputLabel
          label={label}
          name={phoneFieldName}
          labelColor={labelColor}
          hasError={hasError}
          isRequired={isRequired}
          data-qaid={`${qaId}-label`}
        />
        <Box alignItems="center">
          <Grid alignItems="center">
            <GridItem mobileStart={1} row={1} mobile={12}>
              <TextInput
                value={inputValue}
                name={phoneFieldName}
                type="tel"
                ref={inputRef}
                disabled={isDisabled}
                hasError={hasError}
                onBlur={handleBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handlePhoneValueChange(e)
                }
                backgroundColor={inputBackgroundColor}
                pl={paddingLeft}
                invertColors={invertColors}
                aria-required={isRequired}
                data-qaid={`${qaId}-input`}
                {...props}
              />
            </GridItem>
            <GridItem
              mobileStart={1}
              mobile={12}
              row={1}
              position="absolute"
              left={`${ruler[4]}px`}
              alignItems="center"
              borderRadius={borderRadius.input}
              gap={1}
              ref={dropDownAndPrefixRef}
              h={dimensions.input.height}
            >
              <CountrySelector
                countries={countries}
                hideDropdown={false}
                selectedCountry={country.iso2}
                onSelect={({ iso2 }: { iso2: CountryIso2 }) => setCountry(iso2)}
                style={countrySelectorStyle}
                buttonStyle={buttonStyle}
                buttonContentWrapperStyle={buttonContentWrapperStyle}
                dropdownStyleProps={dropdownStyleProps}
                flagStyle={flagStyle}
                dropdownArrowStyle={dropdownArrowStyle}
              />
              <DialCodePreview
                dialCode={country.dialCode}
                prefix="+"
                style={dialCodeStyleProps}
              />
            </GridItem>
          </Grid>
        </Box>
        {(!!helperText || errorMsg) && (
          <InputHelperText
            isDisabled={isDisabled}
            hasError={hasError}
            isSuccessful={isSuccessful}
            invertColors={invertColors}
            data-qaid={`${qaId}-helper-text`}
          >
            {errorMsg ? errorMsg : helperText}
          </InputHelperText>
        )}
      </Box>
    </Box>
  );
};
